import C from "classnames";
import { useLayoutEffect, useRef } from "react";

import { themeClasses as T } from "../../utils";

import S from "./OfflineBanner.module.scss";

/**
 * Persistent notice shown while offline, rendered inside Header (see
 * `showOfflineBanner`)
 */
export const OfflineBanner = (): React.JSX.Element => {
  const bannerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = bannerRef.current;
    const root = document.documentElement;
    if (!el) return;

    // Re-measure on any size change
    const setHeight = () => {
      const marginBottom = parseFloat(getComputedStyle(el).marginBottom) || 0;
      root.style.setProperty("--banner-height", `${el.offsetHeight + marginBottom}px`);
    };
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.setProperty("--banner-height", "0px");
    };
  }, []);

  return (
    <div
      ref={bannerRef}
      className={C(T.bgElevation1, "mb-2 border-b border-surface-border", S.banner)}
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
    >
      You&rsquo;re offline. You can keep browsing what&rsquo;s already loaded; other apps and new
      content will return when you reconnect.
    </div>
  );
};
